import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import FingerprintJS from "@fingerprintjs/fingerprintjs";
import { AudioLines, Sparkles, ShieldCheck, CheckCircle2, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export default function Login() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [guestLoading, setGuestLoading] = useState(false);
  const deviceIdRef = useRef(null);

  // Pre-compute device fingerprint on mount so guest sign-in is instant
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const fp = await FingerprintJS.load();
        const result = await fp.get();
        if (!cancelled) deviceIdRef.current = result.visitorId;
      } catch (err) {
        // Fingerprinting can fail in restrictive browsers; fall back to null device_id
        console.warn("Fingerprint precompute failed:", err?.message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleGuest = async () => {
    setGuestLoading(true);
    try {
      // Ensure fingerprint is ready (compute on demand if useEffect hasn't finished)
      let deviceId = deviceIdRef.current;
      if (!deviceId) {
        try {
          const fp = await FingerprintJS.load();
          const result = await fp.get();
          deviceId = result.visitorId;
          deviceIdRef.current = deviceId;
        } catch (err) {
          console.warn("Fingerprint on-demand failed:", err?.message);
        }
      }
      const res = await api.post("/auth/guest", { device_id: deviceId });
      setUser(res.data);
      navigate("/", { replace: true });
    } catch (err) {
      toast.error("Couldn't start guest session", {
        description: err?.response?.data?.detail || err?.message || "Try again.",
      });
      setGuestLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex flex-col items-center justify-center px-4 sm:px-8 py-10 relative">
      <div className="w-full max-w-md flex flex-col gap-6 relative z-10">
        {/* Brand */}
        <div className="flex items-center gap-2.5 justify-center mb-2">
          <div className="w-10 h-10 rounded-xl bg-[#0A0A0A] border border-white/10 flex items-center justify-center">
            <AudioLines className="w-5 h-5 text-[#FF2D55]" strokeWidth={2.2} />
          </div>
          <span className="font-display font-black text-white text-2xl tracking-tight">
            KeyFinder
          </span>
        </div>

        {/* Login card */}
        <div className="bg-[#0A0A0A] border border-white/5 rounded-3xl p-8 sm:p-10 shadow-2xl">
          <div className="text-center mb-7">
            <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter text-white leading-[0.95]">
              Find the<br />
              <span className="text-[#FF2D55] led-glow">key</span> of any song
            </h1>
            <p className="text-sm text-zinc-400 mt-4 font-body leading-relaxed">
              Get <span className="text-white font-semibold">3 free analyses</span>.
              Subscribe for unlimited.
            </p>
          </div>

          {/* Features */}
          <ul className="space-y-2.5 mb-7">
            {[
              "AI-powered musical key detection",
              "Camelot DJ notation + relative key + BPM",
              "Personal history of every analysis",
            ].map((t, i) => (
              <li key={i} className="flex items-center gap-2.5 text-sm text-zinc-300 font-body">
                <CheckCircle2 className="w-4 h-4 text-[#39FF14] shrink-0" strokeWidth={2.2} />
                <span>{t}</span>
              </li>
            ))}
          </ul>

          {/* Primary: Log In */}
          <button
            onClick={handleLogin}
            data-testid="login-button"
            className="w-full bg-[#FF2D55] text-white hover:bg-[#D11B47] font-bold px-8 py-4 rounded-full text-base font-display tracking-tight transition-all shadow-[0_0_24px_rgba(255,45,85,0.35)] hover:shadow-[0_0_36px_rgba(255,45,85,0.6)] flex items-center justify-center gap-2.5 group"
          >
            <Sparkles className="w-4 h-4" strokeWidth={2.4} />
            <span>Log In</span>
            <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" strokeWidth={2.4} />
          </button>

          {/* Secondary: Guest */}
          <button
            onClick={handleGuest}
            disabled={guestLoading}
            data-testid="guest-login-button"
            className="mt-3 w-full bg-transparent hover:bg-white/5 text-zinc-300 hover:text-white font-medium px-6 py-3 rounded-full text-sm font-display tracking-tight transition-colors flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {guestLoading ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Starting…
              </>
            ) : (
              <span>Continue as guest</span>
            )}
          </button>

          <div className="mt-5 flex items-center justify-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-zinc-600 font-mono">
            <ShieldCheck className="w-3 h-3 text-[#39FF14]" />
            <span>privacy first · no audio stored</span>
          </div>
        </div>

        <p className="text-center text-[11px] text-zinc-600 font-body">
          By continuing you agree that audio is analyzed and discarded immediately. Only key results are stored.
        </p>
      </div>
    </div>
  );
}
